import { windowatch } from '../../../scripts/lib/windowatch';
import { getInstance } from '../../../scripts/lib/views';
import emitter from '../../../scripts/lib/event-emitter';

const name = 'module-filter';
const searchTypingCooldown = 500;
const minQueryLength = 2;

const breakpointPageCount = {
  xs: 4,
  s: 4,
  m: 6,
  l: 8,
  xl: 8
};

export default class FilterModule {

  constructor ($el) {

    this.$el = $el;
    this.type = $el.dataset.type;

    if (this.type === 'jobs') {
      return;
    }

    // state
    this.filters = {};
    this.loading = false;
    this.searchCooldownTimeout = null;
    this.filterSelected = this.$el.classList.contains(name + '--filter-selected');

    // handlers
    this.filterChangeHandler = this.onFilterChange.bind(this);

    // messages
    this.messageNoResults = $el.dataset.messageNoResults;
    this.messageError = $el.dataset.messageError;

    // bind elements
    this.$itemWrapper = $el.querySelector(`.${name}__items`);
    this.$items = [...this.$itemWrapper.children];
    this.$message = $el.querySelector(`.${name}__message`);
    this.$moreButton = $el.querySelector(`.${name}__btn-more`);
    this.$search = $el.querySelector('.filter-search__input');
    this.$selectFilters = [...$el.querySelectorAll('.filter-select')];
    this.$hiddenFilters = [...$el.querySelectorAll(`.${name}__filter-hidden`)];

    // bind events
    this.$moreButton.onclick = this.onMoreButtonClick.bind(this);
    this.$search.oninput = this.onSearchChange.bind(this);

    this.selectFilters = this.$selectFilters.map($selectFilter => {
      const selectFilter = getInstance($selectFilter);
      selectFilter.addChangeListener(this.filterChangeHandler);

      return selectFilter;
    });

    // Run some initialization tasks
    this.init();
  }

  init () {
    // pre-collect hidden filters
    this.$hiddenFilters.forEach($hidden => {
      this.filters[$hidden.name] = $hidden.value;
    });

    // pagination (item count per page) is different between breakpoint
    // throw away items that are not needed for the initial breakpoint
    const initialBreakpoint = windowatch.getBreakpoint();
    const itemCount = breakpointPageCount[initialBreakpoint];

    const originalCount = this.$items.length;

    this.$items = this.$items.filter(($item, index) => {
      if (index >= itemCount) {
        $item.parentNode.removeChild($item);

        return false;
      }

      return true;
    });

    // When items have been thrown away, update the more button visibility
    if (this.$items.length < originalCount) {
      // Make more button visible
      this.$moreButton.classList.remove(`${name}__btn-more--hidden`);
    }

    if (this.filterSelected) {
      // Scroll to the filter module if a filter is pre-selected
      this.scrollToFilters();
    }
  }

  scrollToFilters () {
    // Calculate scroll position by using filter module top and subtract
    // the navigation height
    let nav = document.documentElement.querySelector('.navigation__foreground');
    let scrollTo = this.$el.offsetTop - nav.style.height;

    window.scrollTo({
      top: scrollTo,
      behavior: 'smooth'
    });
  }

  onSearchChange () {

    // clear previous cooldown timeout
    if (this.searchCooldownTimeout !== null) {
      clearTimeout(this.searchCooldownTimeout);
    }

    // schedule cooldown timeout to let the user finish typing
    this.searchCooldownTimeout = setTimeout(() => {
      this.searchCooldownTimeout = null;

      // reset filters
      this.selectFilters.forEach(selectFilter =>
        selectFilter.resetValue(this.filterChangeHandler));

      // trigger filter change
      this.onFilterChange();

    }, searchTypingCooldown);
  }

  async onFilterChange () {

    // collect filter values
    const filters = this.getCurrentFilters();

    // check if filters have changed
    if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
      return;
    }

    // update filters
    this.filters = filters;

    // load first page with given filters
    this.loadPage(0);
  }

  onMoreButtonClick (evt) {

    // collect filter values
    this.filters = this.getCurrentFilters();

    // load next page
    this.loadPage(this.$items.length);
  }

  getCurrentFilters () {
    const filters = {};

    // collect current filter values
    this.selectFilters.forEach(selectFilter => {
      filters[selectFilter.getName()] = selectFilter.getValue();
    });

    // retrieve search query (only consider queries longer than 2 chars)
    const search = this.$search.value;
    if (search.length >= minQueryLength) {
      filters['search'] = search;
    }

    // collect hidden filters
    this.$hiddenFilters.forEach($hidden => {
      filters[$hidden.name] = $hidden.value;
    });

    return filters;
  }

  /**
   * Loads a page of results.
   * @param {number} [offset=0] Item offset
   * @return {Promise} Resolves when finished
   */
  async loadPage (offset = 0) {

    // block page retrievals when already loading a page
    if (this.loading) {
      return;
    }

    // get currently set filters
    const filters = this.filters;

    // set loading state
    this.loading = true;
    this.$moreButton.classList.add('button--disabled');

    if (offset === 0) {

      // existing pages will be replaced, hide them
      this.$itemWrapper.classList.add(`${name}__items--hidden`);
    }

    // get page count
    const breakpoint = windowatch.getBreakpoint();
    let count = breakpointPageCount[breakpoint];

    // fill up previous page when items are missing (due to resizing)
    if (offset > 0 && this.$items.length % count > 0) {
      count += count - (this.$items.length % count);
    }

    // prepare request
    const request = new Request(this.$el.dataset.url, {
      method: 'POST',
      body: JSON.stringify({
        type: this.type,
        filters,
        offset,
        count
      })
    });

    let response = null;
    let content = null;

    try {

      // await response from the server (may take some time)
      response = await fetch(request);
      // interpret response as JSON
      content = await response.json();

    } catch (e) {
      // sad story, we'll deal with it shortly
    }

    // check if filters have changed in the meantime
    if (JSON.stringify(this.filters) !== JSON.stringify(filters)) {
      // stop here and request the first page with updated filters
      this.loading = false;

      return await this.loadPage(0);
    }

    // check result
    const responseValid =
      response !== null && response.ok && content !== null && content.success;
    const responseEmpty = responseValid ? content.data.count === 0 : false;

    if (!responseValid || responseEmpty) {

      this.loading = false;
      this.$moreButton.classList.remove('button--disabled');

      // hide more button when the first page couldn't be loaded
      if (responseEmpty || offset === 0) {

        // empty pages container
        this.$itemWrapper.innerHTML = '';
        this.$items = [];

        // remove more button
        this.$moreButton.classList.add(`${name}__btn-more--hidden`);
      }

      // update copyright notices
      emitter.emit('updateCopyrightNotices');

      if (!responseValid) {

        return this.showMessage(this.messageError);
      }

      return this.showMessage(this.messageNoResults);
    }

    // hide previously displayed message
    this.hideMessage();

    if (offset === 0) {

      // empty pages container
      this.$itemWrapper.innerHTML = '';
      this.$items = [];
    }

    // append page
    this.$itemWrapper.innerHTML += content.data.html;
    this.$items = [...this.$itemWrapper.children];

    // update more button visibility
    const reachedEnd = content.data.reachedEnd;
    this.$moreButton.classList.toggle(`${name}__btn-more--hidden`, reachedEnd);

    // create next cycle
    this.$el.getBoundingClientRect();

    // clear loading state
    this.loading = false;
    this.$moreButton.classList.remove('button--disabled');

    // update copyright notices
    emitter.emit('updateCopyrightNotices');
  }

  showMessage (message) {

    this.$message.innerText = message;
    this.$message.classList.remove(`${name}__message--hidden`);
  }

  hideMessage () {

    this.$message.classList.add(`${name}__message--hidden`);
  }
}
